/**
 * The `run_code` page proxy: human-like click press timing plus an optional
 * before-action hook.
 *
 * **Press timing.** Playwright's `locator.click()` / `mouse.click()` default to
 * `delay: 0` — the synthesized `mousedown` and `mouseup` fire in the *same*
 * event-loop tick. Some apps drive a side effect (e.g. a data refetch on
 * pagination) through a handler that loses a React state race when press and
 * release land in one tick: the click "registers" (URL/UI update) but the
 * follow-on effect never runs. A real human always holds the button for tens of
 * milliseconds, so they never hit it. We give every agent click a small,
 * human-length press by defaulting `delay`. An explicit `delay` always wins.
 *
 * **Before-action hook.** This traversal is the only place that sees every
 * interaction a snippet performs *before* it happens, which makes it the natural
 * seam for the anticipatory cursor cue (see `playwright-client/run-code-cue.ts`).
 * The hook is optional — with none supplied this module behaves exactly as it did
 * when it only injected press delays.
 *
 * Applied at the shared choke point so it covers BOTH execution modes:
 *   - full agent mode — the executor passes {@link HUMAN_CLICK_PRESS_DELAY_MS} in
 *     its click options (see playwright-client `click`);
 *   - fast mode — {@link applyHumanClickDelay} wraps the `page` handed to the
 *     `run_code` primitive (live fast-drive AND deterministic cache replay), so
 *     cached `locator.click()` calls inherit both the delay and the cue.
 *
 * @module
 */

import type { Page } from 'playwright';

/**
 * Default mousedown→mouseup hold for agent clicks, in milliseconds. Long enough
 * to cross an event-loop/frame boundary (defeating same-tick click races), far
 * below any double-click (~500ms) or long-press threshold.
 */
export const HUMAN_CLICK_PRESS_DELAY_MS = 100;

/**
 * What kind of Playwright object an intercepted call is being made on.
 *
 * Explicit kinds rather than "has a callable `click`" — that test lumps together
 * `Locator`, `ElementHandle`, `Frame`, and `Page` despite wildly different
 * semantics (`Page.click(selector)` is a legacy multi-match selector API;
 * `Locator.click()` is strict and has a bounding box). Only the kinds that
 * genuinely have element bounding-box semantics may reach an element cue.
 */
export type ActionTargetKind =
  | 'locator'
  | 'element-handle'
  | 'frame-locator'
  | 'page'
  | 'frame'
  | 'mouse'
  | 'keyboard'
  /**
   * A traversal-only container (today: `BrowserContext`). It has no actions of
   * its own, but it hands out `Page`s — so it must still be wrapped, or
   * `page.context().pages()[0]` would be a RAW page whose clicks bypass the cue,
   * the press delay, and the abort refusal entirely.
   */
  | 'container'
  | 'other';

/** Whether the intercepted action may proceed. */
export type BeforeActionOutcome = 'proceed' | 'aborted';

/** A rectangle in main-frame viewport coordinates. */
export interface ActionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BeforeActionContext {
  kind: ActionTargetKind;
  /** The method about to be invoked, e.g. `click`, `fill`, `press`. */
  method: string;
  /** The arguments it was called with, untouched. */
  args: readonly unknown[];
  /**
   * The bounding-box-capable target — present ONLY for `locator` and
   * `element-handle`. `Frame` and `Page` never populate this: a legacy selector
   * API has no single resolved element to cue.
   */
  element?: { boundingBox(options?: { timeout?: number }): Promise<ActionBox | null> };
}

/**
 * Runs before the underlying Playwright call. Returning `aborted` prevents
 * dispatch entirely — the cancellation checkpoint that `executeRunCode`'s
 * race-without-cancel otherwise lacks.
 */
export type BeforeActionHook = (ctx: BeforeActionContext) => Promise<BeforeActionOutcome>;

/** Thrown when the hook refuses dispatch. Recognized by `executeRunCode`. */
export const ABORTED_BEFORE_DISPATCH = 'runCode aborted before dispatch';

/** Click/dblclick option bags accept an optional `delay`. */
type ClickOpts = { delay?: number } & Record<string, unknown>;

/** Default `delay` unless the caller set one explicitly (0 included — intent wins). */
function withPressDelay(opts?: ClickOpts): ClickOpts {
  if (opts && typeof opts.delay === 'number') {
    return opts;
  }
  return { ...opts, delay: HUMAN_CLICK_PRESS_DELAY_MS };
}

function has(v: object, key: string): boolean {
  return typeof (v as Record<string, unknown>)[key] === 'function';
}

/**
 * Classify a Playwright object structurally.
 *
 * Order matters: `Page` and `Frame` are checked FIRST precisely because they also
 * expose `click` — misclassifying either as an element is how a legacy selector
 * API would end up cued as though it were one resolved element.
 */
export function classifyTarget(v: unknown): ActionTargetKind {
  if (!v || typeof v !== 'object') return 'other';
  const o = v as object;
  // Element check FIRST — it is both the hot path (nearly everything a snippet
  // chains is a Locator) and unambiguous: neither Page nor Frame has a
  // boundingBox, so this can never swallow a legacy selector API.
  if (has(o, 'boundingBox') && has(o, 'click')) {
    // Locator has the chainers; ElementHandle has `asElement`/`ownerFrame`.
    return has(o, 'first') || has(o, 'nth') ? 'locator' : 'element-handle';
  }
  // Page owns both input devices; Frame owns neither.
  if (has(o, 'goto') && has(o, 'locator')) {
    return 'mouse' in o && 'keyboard' in o ? 'page' : 'frame';
  }
  if (has(o, 'childFrames')) return 'frame';
  if (has(o, 'wheel') && has(o, 'move') && has(o, 'down')) return 'mouse';
  if (has(o, 'insertText') && has(o, 'press')) return 'keyboard';
  // BrowserContext: hands out Pages, so it must stay inside the wrapper.
  if (has(o, 'newPage') && has(o, 'pages')) return 'container';
  // Browser: `page.context().browser()!.newPage()` would otherwise hand back a
  // RAW page whose interactions bypass the cue and the abort refusal.
  if (has(o, 'newContext') && has(o, 'contexts')) return 'container';
  // Event payloads that can navigate BACK to a Page or Frame — Request, Response,
  // ConsoleMessage, Download, Dialog, FileChooser, Worker. Without this,
  // `page.on('request', r => r.frame().locator(…))` escapes the proxy entirely.
  // Checked last: Locator and Frame also expose `page()`, and both are already
  // classified above.
  if (has(o, 'frame') || has(o, 'page')) return 'container';
  // A locator factory with no click of its own — FrameLocator.
  if (!has(o, 'click') && (has(o, 'locator') || has(o, 'getByRole'))) return 'frame-locator';
  // Locator-shaped object without boundingBox (test fakes, older stubs): still a
  // click target for delay purposes, and `element` cueing tolerates a missing box.
  if (has(o, 'click')) return 'locator';
  return 'other';
}

/** Element-targeted methods worth cueing. */
const ELEMENT_CUE_METHODS = new Set([
  'click',
  'dblclick',
  'tap',
  'hover',
  'fill',
  'type',
  'pressSequentially',
  'press',
  'check',
  'uncheck',
  'selectOption',
  'clear',
]);

/** Coordinate methods on `page.mouse`. */
const MOUSE_CUE_METHODS = new Set(['click', 'dblclick', 'move']);

/** Keyboard methods that should raise a keycap. */
const KEYBOARD_CUE_METHODS = new Set(['press', 'type', 'insertText', 'down']);

/** Methods that take a listener which may be handed raw Playwright objects. */
const LISTENER_METHODS = new Set([
  'on',
  'once',
  'addListener',
  'prependListener',
  'prependOnceListener',
]);

/**
 * Removal counterparts. These must translate the caller's ORIGINAL function back
 * to the wrapper that was actually registered — otherwise `page.off('popup', fn)`
 * silently fails to remove anything and the listener keeps firing.
 */
const LISTENER_REMOVAL_METHODS = new Set(['off', 'removeListener']);

/** Kinds whose clicks get the human press delay. Legacy Page/Frame clicks do not. */
const DELAY_KINDS = new Set<ActionTargetKind>(['locator', 'element-handle', 'mouse']);

/**
 * Legacy `Page`/`Frame` selector actions. These are never *cued* — a multi-match
 * selector API has no single resolved element to point at — but they still run the
 * hook so its abort checkpoints apply. Without this they would keep the orphan
 * behavior where a snippet past its deadline still dispatches a real action.
 */
const LEGACY_ACTION_METHODS = new Set([
  'click',
  'dblclick',
  'tap',
  'hover',
  'fill',
  'type',
  'press',
  'check',
  'uncheck',
  'selectOption',
]);

/**
 * Whether the before-action hook runs for this call. Broader than "is cued" on
 * purpose: the hook is also the abort checkpoint, so it covers actions that draw
 * nothing.
 */
function shouldHook(kind: ActionTargetKind, method: string, args: readonly unknown[]): boolean {
  // `trial: true` deliberately performs no action — cueing it would animate an
  // interaction that never happens.
  const opts = args.find((a) => a && typeof a === 'object' && !Array.isArray(a)) as
    | Record<string, unknown>
    | undefined;
  if (opts?.trial === true) return false;
  switch (kind) {
    case 'locator':
    case 'element-handle':
      return ELEMENT_CUE_METHODS.has(method);
    case 'mouse':
      return MOUSE_CUE_METHODS.has(method);
    case 'keyboard':
      return KEYBOARD_CUE_METHODS.has(method);
    case 'page':
    case 'frame':
      return LEGACY_ACTION_METHODS.has(method);
    // FrameLocator is a pure factory — nothing to guard.
    default:
      return false;
  }
}

interface WrapOptions {
  beforeAction?: BeforeActionHook;
  /**
   * Memoizes target → proxy so wrapping is idempotent and identity is stable:
   * without it every `page.mouse` read minted a fresh proxy, so
   * `page.mouse !== page.mouse`, and re-wrapping a cycle (`locator.page()`)
   * would nest proxies without bound.
   */
  cache?: WeakMap<object, object>;
  /**
   * Maps a caller's listener to the wrapper actually registered with Playwright,
   * so `off`/`removeListener` can find it and a repeat registration reuses it.
   *
   * Scoped by EMITTER and then by event name, not global: one options bag is
   * shared by every proxied object, so a flat map would let a wrapper registered
   * for `fn` on one Page be substituted when removing `fn` from a different
   * emitter — potentially removing the wrong listener, or failing to remove one
   * that was registered outside this proxy.
   */
  listeners?: WeakMap<object, Map<string, Map<ListenerFn, ListenerFn[]>>>;
}

type ListenerFn = (...a: unknown[]) => unknown;

/**
 * Per-emitter, per-event listener registry, created on first use.
 *
 * The value is a STACK, not a single wrapper: registering the same function
 * twice adds two entries to the emitter, so two removals must drain two entries.
 * Deleting the mapping on the first removal left the second `off()` passing the
 * original function, which removed nothing.
 */
function listenerMapFor(opts: WrapOptions, target: object, event: string): Map<ListenerFn, ListenerFn[]> {
  let perTarget = opts.listeners?.get(target);
  if (!perTarget) {
    perTarget = new Map();
    opts.listeners?.set(target, perTarget);
  }
  let perEvent = perTarget.get(event);
  if (!perEvent) {
    perEvent = new Map();
    perTarget.set(event, perEvent);
  }
  return perEvent;
}

/** True when a computed transform is a plain translate/positive-scale. */


function isPromiseLike(v: unknown): v is PromiseLike<unknown> {
  return !!v && typeof v === 'object' && typeof (v as { then?: unknown }).then === 'function';
}

function rewrapResolved<T>(res: T, opts: WrapOptions): T {
  if (Array.isArray(res)) return res.map((item) => rewrapResolved(item, opts)) as T;
  if (!res || typeof res !== 'object') return res;
  const kind = classifyTarget(res);
  if (kind === 'other') return res;
  return wrapTarget(res as unknown as object, kind, opts) as unknown as T;
}

/** Re-wrap any Playwright object returned by a chained call so behavior propagates. */
function rewrap<T>(res: T, opts: WrapOptions): T {
  if (isPromiseLike(res)) {
    const wrapped = Promise.resolve(res).then((value) => rewrapResolved(value, opts));
    void wrapped.catch(() => {
      // Preserve rejection for awaited callers while marking abandoned Playwright
      // operations as handled so model-authored snippets cannot leak process-level
      // unhandledRejection noise after run_code has already moved on.
    });
    return wrapped as T;
  }
  return rewrapResolved(res, opts);
}

/**
 * Inject the press delay, accounting for `page.mouse`'s positional `(x, y, opts)`
 * signature versus a locator's `(opts)`.
 */
function applyDelayToArgs(kind: ActionTargetKind, args: unknown[]): unknown[] {
  if (kind === 'mouse') {
    const [x, y, o] = args as [number, number, ClickOpts | undefined];
    return [x, y, withPressDelay(o)];
  }
  return [withPressDelay(args[0] as ClickOpts | undefined)];
}

/**
 * Proxy any Playwright object: run the before-action hook for cueable methods,
 * inject the press delay on clicks, and re-wrap chained results.
 */
function wrapTarget<T extends object>(target: T, kind: ActionTargetKind, opts: WrapOptions): T {
  const cached = opts.cache?.get(target);
  if (cached) return cached as T;
  const proxy = buildProxy(target, kind, opts);
  opts.cache?.set(target, proxy);
  return proxy;
}

function buildProxy<T extends object>(target: T, kind: ActionTargetKind, opts: WrapOptions): T {
  // Captured so a wrapped listener can be invoked with the PROXY as `this`.
  // Forwarding the native `this` handed back the RAW emitter — in production the
  // request guard binds `on()` to the underlying page — which let a listener do
  // `this.locator(…).click()` and escape the cue and abort refusal entirely.
  let proxy: T;
  proxy = new Proxy(target, {
    get(obj, prop, receiver) {
      const value = Reflect.get(obj, prop, receiver);
      if (typeof prop !== 'string') return value;

      if (typeof value !== 'function') {
        // `page.mouse` / `page.keyboard` are objects, not functions — wrap them so
        // coordinate and keyboard actions are cued too.
        if (value && typeof value === 'object' && (prop === 'mouse' || prop === 'keyboard')) {
          return wrapTarget(value as object, prop === 'mouse' ? 'mouse' : 'keyboard', opts);
        }
        return value;
      }

      const method = prop;
      const wantsDelay =
        (method === 'click' || method === 'dblclick') && DELAY_KINDS.has(kind);
      const fn = value as (...a: unknown[]) => unknown;

      // Event listeners receive raw Playwright objects — `page.on('popup', p =>
      // …)` hands back an unwrapped Page whose clicks would bypass the cue, the
      // press delay, and the abort refusal. (`waitForEvent` is already covered:
      // it returns a promise, which `rewrap` unwraps.) Wrap the listener so its
      // arguments come back through the same memoized wrapper — and memoize the
      // wrapper itself, so removal still works and re-registering the same
      // function does not mint a second, unrelated wrapper.
      if (LISTENER_METHODS.has(method) || LISTENER_REMOVAL_METHODS.has(method)) {
        const removing = LISTENER_REMOVAL_METHODS.has(method);
        return (...args: unknown[]) => {
          const event = typeof args[0] === 'string' ? args[0] : '';
          const registry = listenerMapFor(opts, obj, event);
          const mapped = args.map((a, i) => {
            if (i === 0 || typeof a !== 'function') return a;
            const original = a as ListenerFn;
            const stack = registry.get(original);
            if (removing) {
              // Pop ONE registration per removal so duplicates drain fully.
              const wrapper = stack?.pop();
              if (stack && stack.length === 0) registry.delete(original);
              // Nothing of ours registered: pass the original through untouched
              // rather than inventing a wrapper that was never added (it may
              // have been registered outside this proxy).
              return wrapper ?? original;
            }
            // Reuse one wrapper identity per (emitter, event, listener) so the
            // emitter's own duplicate-detection behaves as the caller expects.
            const wrapper =
              stack?.[0] ??
              function (this: unknown, ...cbArgs: unknown[]) {
                // `this` is the PROXY, never the raw emitter — see `proxy` above.
                return original.apply(
                  proxy,
                  cbArgs.map((c) => rewrapResolved(c, opts))
                );
              };
            if (stack) stack.push(wrapper);
            else registry.set(original, [wrapper]);
            return wrapper;
          });
          fn.apply(obj, mapped);
          // EventEmitter registration/removal is specified to return the emitter.
          // Do NOT rewrap the return value: in production the request guard binds
          // these methods to the RAW page, so what comes back is not the object in
          // our cache, and rewrapping it would mint a SECOND facade with its own
          // listener registry — breaking `page.on(e, fn).off(e, fn)`. Return the
          // proxy the caller already holds.
          return proxy;
        };
      }

      return (...args: unknown[]) => {
        const callArgs = wantsDelay ? applyDelayToArgs(kind, args) : args;
        const invoke = () => rewrap(fn.apply(obj, callArgs), opts);

        const hook = opts.beforeAction;
        if (!hook || !shouldHook(kind, method, args)) return invoke();

        return (async () => {
          let outcome: BeforeActionOutcome = 'proceed';
          try {
            outcome = await hook({
              kind,
              method,
              args,
              element:
                kind === 'locator' || kind === 'element-handle'
                  ? (obj as unknown as BeforeActionContext['element'])
                  : undefined,
            });
          } catch {
            // A cue is decoration. A bug in it must never turn into a failed
            // action, so a throwing hook proceeds as though it had said so.
            outcome = 'proceed';
          }
          if (outcome === 'aborted') throw new Error(ABORTED_BEFORE_DISPATCH);
          return invoke();
        })();
      };
    },
  });
  return proxy;
}

/**
 * Wrap a Playwright `Page` so every click issued through it — directly
 * (`page.mouse.click`), via a locator factory (`page.getByRole(...).click()`), or
 * through a chained/framed locator — gets a default human-length press delay, and
 * (when `beforeAction` is supplied) an anticipatory cue before dispatch.
 * Transparent for everything else.
 */
export function applyHumanClickDelay(page: Page, opts?: WrapOptions): Page {
  const withCache: WrapOptions = {
    ...opts,
    cache: opts?.cache ?? new WeakMap<object, object>(),
    listeners: opts?.listeners ?? new WeakMap(),
  };
  return wrapTarget(page as unknown as object, 'page', withCache) as unknown as Page;
}
